"use client"

import * as React from 'react'
import { useRouter, useParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

const WAGON_TYPE_OPTIONS = [
  { value: 'flat', label: 'Flat' },
  { value: 'tank', label: 'Tank' },
  { value: 'hopper', label: 'Hopper' },
  { value: 'box', label: 'Box' },
  { value: 'intermodal', label: 'Intermodal' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

type WagonData = {
  id: string
  evn: string
  name: string
  series: string
  wagon_type: string
  status: string
  weight_tonnes: number
  max_load_tonnes: number | null
  length_metres: number | null
  ecm_name: string
  ecm_certificate_id: string | null
  ecm_valid_until: string | null
  notes: string | null
}

type RouteParams = {
  id?: string | string[]
  slug?: string[]
}

function readRouteId(params?: RouteParams | null): string {
  if (!params) return ''
  if (typeof params.id === 'string') return params.id
  if (Array.isArray(params.id)) return params.id[0] ?? ''
  if (Array.isArray(params.slug)) return params.slug.at(-1) ?? ''
  return ''
}

export default function EditWagonPage({ params }: { params?: RouteParams }) {
  const t = useT()
  const router = useRouter()
  const routeParams = useParams()
  const id = readRouteId(params) || readRouteId(routeParams as RouteParams | undefined)
  const scopeVersion = useOrganizationScopeVersion()

  const [wagon, setWagon] = React.useState<WagonData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!id) {
      setWagon(null)
      setLoadError(t('rolling_stock.wagons.detail.error.load', 'Wagon not found'))
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function load() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback = { items: [] as WagonData[] }
        const res = await apiCall<{ items: WagonData[]; error?: string }>(
          `/api/rolling-stock/wagons?ids=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (cancelled) return

        if (!res.ok) {
          setWagon(null)
          setLoadError(
            typeof res.result?.error === 'string'
              ? res.result.error
              : t('rolling_stock.wagons.detail.error.load', 'Failed to load wagon'),
          )
          return
        }

        const item = Array.isArray(res.result?.items) ? res.result.items[0] : null
        if (item) {
          setWagon(item)
          return
        }

        setWagon(null)
        setLoadError(t('rolling_stock.wagons.detail.error.load', 'Wagon not found'))
      } catch (error) {
        if (cancelled) return
        setWagon(null)
        setLoadError(
          error instanceof Error && error.message
            ? error.message
            : t('rolling_stock.wagons.detail.error.load', 'Failed to load wagon'),
        )
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [id, scopeVersion, t])

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'evn', label: t('rolling_stock.wagons.fields.evn', 'EVN (12 digits)'), type: 'text', required: true, maxLength: 12 },
    { id: 'name', label: t('rolling_stock.wagons.fields.name', 'Name'), type: 'text', required: true },
    { id: 'series', label: t('rolling_stock.wagons.fields.series', 'Series'), type: 'text', required: true },
    { id: 'wagonType', label: t('rolling_stock.wagons.fields.wagonType', 'Wagon Type'), type: 'select', required: true, options: WAGON_TYPE_OPTIONS },
    { id: 'status', label: t('rolling_stock.wagons.fields.status', 'Status'), type: 'select', required: true, options: STATUS_OPTIONS },
    { id: 'weightTonnes', label: t('rolling_stock.wagons.fields.weightTonnes', 'Tare Weight (tonnes)'), type: 'number', required: true, min: 0 },
    { id: 'maxLoadTonnes', label: t('rolling_stock.wagons.fields.maxLoadTonnes', 'Max Load (tonnes)'), type: 'number', min: 0 },
    { id: 'lengthMetres', label: t('rolling_stock.wagons.fields.lengthMetres', 'Length over buffers (m)'), type: 'number', min: 0 },
    { id: 'ecmName', label: t('rolling_stock.wagons.fields.ecmName', 'ECM Name'), type: 'text', required: true },
    { id: 'ecmCertificateId', label: t('rolling_stock.wagons.fields.ecmCertificateId', 'ECM Certificate ID'), type: 'text' },
    { id: 'ecmValidUntil', label: t('rolling_stock.wagons.fields.ecmValidUntil', 'ECM Valid Until'), type: 'date' },
    { id: 'notes', label: t('rolling_stock.wagons.fields.notes', 'Notes'), type: 'textarea' },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('rolling_stock.wagons.groups.identification', 'Identification'),
      fields: ['evn', 'name', 'series', 'wagonType', 'status'],
    },
    {
      id: 'dimensions',
      title: t('rolling_stock.wagons.groups.dimensions', 'Dimensions & Capacity'),
      fields: ['weightTonnes', 'maxLoadTonnes', 'lengthMetres'],
    },
    {
      id: 'ecm',
      title: t('rolling_stock.wagons.groups.ecm', 'Entity in Charge of Maintenance'),
      fields: ['ecmName', 'ecmCertificateId', 'ecmValidUntil'],
    },
    {
      id: 'other',
      title: t('rolling_stock.wagons.groups.other', 'Other'),
      fields: ['notes'],
    },
  ], [t])

  if (isLoading) return <LoadingMessage label="" />
  if (loadError || !wagon) return <ErrorMessage label={loadError ?? 'Not found'} />

  const initialValues = {
    evn: wagon.evn,
    name: wagon.name,
    series: wagon.series,
    wagonType: wagon.wagon_type,
    status: wagon.status,
    weightTonnes: wagon.weight_tonnes,
    maxLoadTonnes: wagon.max_load_tonnes ?? undefined,
    lengthMetres: wagon.length_metres ?? undefined,
    ecmName: wagon.ecm_name,
    ecmCertificateId: wagon.ecm_certificate_id ?? undefined,
    ecmValidUntil: wagon.ecm_valid_until ?? undefined,
    notes: wagon.notes ?? undefined,
  }

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rolling_stock.wagons.edit.title', 'Edit Wagon')}
          backHref="/backend/rolling-stock/wagons"
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          submitLabel={t('rolling_stock.wagons.edit.submit', 'Save Changes')}
          cancelHref="/backend/rolling-stock/wagons"
          onDelete={async () => {
            await deleteCrud('rolling-stock/wagons', { id: wagon.id })
            flash(t('rolling_stock.wagons.flash.deleted', 'Wagon deleted'), 'success')
            router.push('/backend/rolling-stock/wagons')
          }}
          deleteRedirect="/backend/rolling-stock/wagons"
          onSubmit={async (values) => {
            await updateCrud('rolling-stock/wagons', { id: wagon.id, ...values })
            flash(t('rolling_stock.wagons.flash.updated', 'Wagon updated'), 'success')
            router.push('/backend/rolling-stock/wagons')
          }}
        />
      </PageBody>
    </Page>
  )
}
